"use client";

import React from "react";
import { Button } from "../common/button";
import { Card } from "../common/card";

export interface TherapistTier {
  level: string;
  priceRange: string;
  priceDescription: string;
  mainDescription: string;
  details: string[];
  qualifications: string[];
  popular?: boolean;
}

export interface TherapistTiersProps {
  title?: string;
  subtitle?: string;
  tiers: TherapistTier[];
  onGetStarted?: (tier: string) => void;
  showComparison?: boolean;
}

export function TherapistTiers({
  title = "Choose Your Therapist Tier",
  subtitle = "Select the therapist level that best matches your needs and budget",
  tiers,
  onGetStarted,
  showComparison = true
}: TherapistTiersProps) {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
          {title}
        </h2>
        <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
          {subtitle}
        </p>
        
        <div className="grid md:grid-cols-2 gap-8">
          {tiers.map((tier, idx) => (
            <div key={idx} className="relative">
              <Card className="p-8 hover:shadow-lg transition-all duration-300 bg-white h-full flex flex-col border hover:border-gray-300">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{tier.level}</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-gray-900">{tier.priceRange}</span>
                  </div>
                  <p className="text-sm text-gray-500">{tier.priceDescription}</p>
                </div>
                
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <p className="text-base text-gray-700 leading-relaxed">{tier.mainDescription}</p>
                </div>
                
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    IDEAL FOR
                  </h4>
                  <div className="space-y-2">
                    {tier.details.map((detail, dIdx) => (
                      <p key={dIdx} className="text-sm text-gray-600 leading-relaxed pl-6">{detail}</p>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8 flex-grow">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    QUALIFICATIONS
                  </h4>
                  <ul className="space-y-2.5">
                    {tier.qualifications.map((qual, qIdx) => (
                      <li key={qIdx} className="text-sm text-gray-600 flex items-start">
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="leading-relaxed">{qual}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  size="lg" 
                  className="w-full mt-auto font-semibold"
                  onClick={() => onGetStarted?.(tier.level)}
                >
                  Get Started with {tier.level.split(' ')[0]}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Button>
              </Card>
            </div>
          ))}
        </div>
        
        {/* Comparison helper */}
        {showComparison && (
          <div className="mt-12 bg-gray-50 rounded-lg p-6 max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Quick Comparison</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Choose Intern Therapists if:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You're dealing with everyday stress and life transitions
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You want affordable therapy within a tight budget
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You're looking for general mental wellness support
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Choose Associate Therapists if:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You're dealing with complex mental health challenges
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You need specialized treatment (trauma, PTSD, etc.)
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    You prefer a therapist with more clinical experience
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}